import React, { useState, useMemo } from 'react'
import { User } from 'lucide-react'
import { getGravatarUrl } from '@/utils/gravatar'

interface AvatarProps {
  email?: string
  name?: string
  avatarUrl?: string
  size?: number
  className?: string
}

/**
 * Avatar component with Gravatar support and Lucide icon fallback
 *
 * Priority order:
 * 1. Custom avatarUrl (if provided)
 * 2. Gravatar (if email provided and exists)
 * 3. User icon or first letter fallback
 */
export const Avatar: React.FC<AvatarProps> = ({
  email,
  name,
  avatarUrl,
  size = 32,
  className = '',
}) => {
  const [gravatarError, setGravatarError] = useState(false)

  // Generate Gravatar URL for email
  // Use 4x size for high-DPI displays
  const gravatarUrl = useMemo(() => {
    if (email) {
      return getGravatarUrl(email, { size: size * 4, default: '404' })
    }
    return null
  }, [email, size])

  // Generate UI Avatars fallback URL
  // const uiAvatarUrl = useMemo(() => {
  //   if (email || name) {
  //     const displayName = name || email || ''
  //     return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&size=${size * 4}&background=e5e7eb&color=1f2937&bold=true`
  //   }
  //   return null
  // }, [email, name, size])

  // Determine which image to show
  const imageSrc = avatarUrl || (!gravatarError ? gravatarUrl : null)

  return (
    <div
      className={`rounded-full bg-gray-200 dark:bg-muted text-gray-700 dark:text-muted-foreground flex items-center justify-center font-semibold text-xs flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={name || email || 'Avatar'}
          className="w-full h-full rounded-full object-cover"
          onError={() => setGravatarError(true)}
        />
      ) : (
        <User className="w-4 h-4" />
      )}
    </div>
  )
}
